// Auto-generated from 'DCIC CLASS Student Manual.docx' -- the Dominion
// Chapel International Churches (D.C.I.C.) foundation/membership and
// discipleship class manual. Structure: 4 sections matching the manual's
// own Week/Section grouping, 16 lectures (one per manual 'Topic'), each
// with its original core teaching as textContent (plus its original 10
// discussion questions, preserved as a "Reflection Questions" reading list
// for group/facilitator use) and a 5-question graded multiple-choice quiz
// converted from those same discussion questions, with one correct answer
// each and a short explanation citing the source teaching/scripture.
//
// A cumulative final assessment (finalQuiz) pulls one representative
// question from each of the 16 topics, spanning all 4 sections, for a
// single graded checkpoint at the end of the whole class.
//
// This is consumed the same way as the hand-written entries in
// courseTemplates.js -- see that file's header comment for the shape.


export const dcicFoundationClassTemplate = {
  "id": "dcic-foundation-discipleship",
  "title": "Foundation & Membership / Discipleship Class",
  "description": "Dominion Chapel International Churches' (D.C.I.C.) foundation and membership class -- a discipleship course covering the church's vision and history, a believer's identity in Christ, core Christian practices, and how to find your place of service.",
  "longDescription": "This course walks a new believer or new member through the same foundation and membership class taught at Dominion Chapel International Churches (D.C.I.C.). Founded in 1987 as a small Bible study of two or three people in Bwari, Abuja, D.C.I.C. has grown into a global ministry of more than 50 branches, built on the vision of raising a \"people of power\" grounded in Genesis 1:26 -- the Dominion Mandate.\n\nAcross four sections you'll move from the ministry's story and vision, into what it means to be justified and identified in Christ, through the core practices of communion, water baptism, baptism of the Holy Spirit, and giving, and finally into finding your own place of service within the church's departments and ministry arms. Every lecture keeps its original discussion questions, workgroup task, and life-application tips for use in a small group or class setting, alongside the core teaching and Scriptures (KJV & AMP).",
  "category": "faith-life",
  "level": "Beginner",
  "duration": "4 weeks",
  "isPaid": false,
  "price": 0,
  "thumbnail": "https://images.unsplash.com/photo-1445633629932-0029acc44e88?auto=format&fit=crop&w=1000&q=60",
  "whatYouLearn": [
    "D.C.I.C.'s history, vision, and the Dominion Mandate",
    "Your justification, identity, and standing as a believer in Christ",
    "The meaning and practice of communion, water baptism, and baptism of the Holy Spirit",
    "Biblical tithing and giving as covenant obedience and worship",
    "How to discover your spiritual gifts and find your place of service in a department"
  ],
  "requirements": [
    "An open heart and a Bible (KJV or AMP)",
    "Enrollment in (or completion of) the DCIC foundation/membership class, in person or online"
  ],
  "tags": [
    "discipleship",
    "bible study",
    "church",
    "foundation class",
    "christian living",
    "dcic"
  ],
  "sections": [
    {
      "title": "Introduction",
      "lectures": [
        {
          "title": "Topic 1: The Journey Since 1989",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nEvery great oak was once a tiny seed that refused to stay buried. The story of Dominion Chapel is not merely a history lesson—it is a living testimony that God still uses humble beginnings to establish kingdom legacies.\n\nCore Teaching:\n\nIn 1987, what began as a small Bible Study fellowship in Bwari, a quiet suburb of Abuja, would become the seedbed of a global ministry. Brother John Praise, then a young graduate awaiting his National Youth Service, had already received a divine vision in 1986: to raise a people of power who would exercise authority over the kingdom of darkness. After his service year in 1988, he returned to Abuja with his wife, Sister Hope, and nurtured that small fellowship into a vibrant community of faith.\n\nBy January 1989, the Kubwa branch was birthed in an uncompleted building. On March 26, 1989, Brother John was ordained as a Pastor by Bishop David Oyedepo at Living Faith Church, Kaduna. The Garki Church followed on April 10, 1989. By December 2, 1989, Dominion Chapel was officially inaugurated at the old parade ground in Abuja. Before then, the congregation had worshipped under the Nicon Bridge—an open-air sanctuary where tractors, cattle, and the elements were constant reminders of their raw faith. From that \"under-bridge church,\" the ministry moved to Church Village in Garki Area 1, where members and their Pastor carried mud blocks on their heads to build a pencil church. Today, the International Headquarters stands at Dominion Villa, a permanent site secured by God's faithfulness.\n\nThe ministry has grown to over 50 branches across Nigeria and in nations including the United Kingdom, the United States, Liberia, Kenya, Cameroon, Uganda, Pakistan, and South Africa. The ministry also runs Dominion Bible Faith Institute (DOBFI), John Praise World Outreach (JOPWO), International Leadership Conferences of Ministries (ILCOM), the Weapons of Influence School of Deliverance, and the annual Festival of Music International (FOMI). From two or three gathered in Bwari to thousands across continents—this is the God of increase at work.\n\nLesson Content:\n\nEvery great work of God begins small. Dominion Chapel International Churches started in 1987 as a Bible study of two or three people in Bwari, a suburb of Abuja. Archbishop John Praise Daniel received a vision in 1986 to raise a people of power who would rule over the kingdom of darkness. In January 1989, the Kubwa branch began in an uncompleted building. On March 26, 1989, Bishop David Oyedepo ordained Brother John Praise as a Pastor. By December 2, 1989, Dominion Chapel was officially inaugurated at the Old Parade Ground, after holding services under the Nicon Bridge—where worshippers carried mud blocks on their heads to build the first church structure.\n\nFrom pencil church to Dominion Villa (1999), and now to over 50 branches across Nigeria, London, USA, Liberia, Kenya, Cameroon, Uganda, Pakistan, and South Africa—this is the journey of faith. The ministry also runs DOBFI (Bible School), JOPWO (world outreach), ILCOM (leadership conferences), the Weapons of Influence School of Deliverance, and FOMI (Festival of Music International). God is faithful.\n\nKey Scriptures (KJV & AMP):\n\n\"For who hath despised the day of small things?\" — Zechariah 4:10 (KJV)\n\n\"For who has despised the day of small things? For these seven shall rejoice when they see the plummet in the hand of Zerubbabel.\"\n\n— Zechariah 4:10 (AMP)\n\n\"Being confident of this very thing, that he which hath begun a good work in you will perform it until the day of Jesus Christ.\" — Philippians 1:6 (KJV)\n\n\"And I am convinced and sure of this very thing, that He Who began a good work in you will continue until the day of Jesus Christ, developing and perfecting that good work.\" — Philippians 1:6 (AMP)\n\nPut It Into Practice\n\nWorkgroup Task: In a mixed-age group, create a \"Then vs. Now\" story using one picture, one testimony, and one Scripture.\n\nLife Application Tips:\n\n- Interview an older member about the early days of DCIC.\n- Write down three lessons from the church's history that apply to your life.\n\nReflection Questions (for group discussion):\n\n1. Why do you think God often starts His greatest works in humble places?\n2. What does the \"mustard seed\" teach you about your own dreams?\n3. How did obedience shape the destiny of this ministry?\n4. What can we learn from carrying \"mud blocks\" for God's house?\n5. Why is it important to know your church's history?\n6. How does the testimony of Archbishop John Praise Daniel inspire you?\n7. In what ways can you \"carry blocks\" for DCIC today?\n8. What role does perseverance play when facing challenges?\n9. How has DCIC's global expansion fulfilled the Great Commission?\n10. What legacy do you want to leave for the next generation?",
          "quiz": {
            "title": "Topic 1 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "In what year did Brother John Praise receive the vision that birthed DCIC?",
                "type": "mcq",
                "options": [
                  "1985",
                  "1986",
                  "1987",
                  "1988",
                  "1989"
                ],
                "correctAnswer": 1,
                "explanation": "The founding vision was received in 1986, a year before the Bwari Bible study began in 1987."
              },
              {
                "questionText": "Where did DCIC begin as a small Bible study fellowship?",
                "type": "mcq",
                "options": [
                  "Garki, Area 1",
                  "Kubwa",
                  "Bwari",
                  "Church Village",
                  "Old Parade Ground"
                ],
                "correctAnswer": 2,
                "explanation": "DCIC began as a small Bible study fellowship of two or three people in Bwari, a suburb of Abuja."
              },
              {
                "questionText": "Who ordained Brother John Praise as pastor on March 26, 1989?",
                "type": "mcq",
                "options": [
                  "Archbishop Benson Idahosa",
                  "Bishop David Oyedepo",
                  "Bishop Iyke Afoenyi",
                  "Bishop Tope Osinowo",
                  "Bishop Eyong"
                ],
                "correctAnswer": 1,
                "explanation": "Bishop David Oyedepo ordained Brother John Praise as a pastor at Living Faith Church, Kaduna."
              },
              {
                "questionText": "What nickname did the church earn while worshipping under the NICON bridge?",
                "type": "mcq",
                "options": [
                  "The Pencil Church",
                  "The Under-Bridge Church",
                  "The Mustard Seed Church",
                  "Dominion Villa",
                  "The Village Church"
                ],
                "correctAnswer": 1,
                "explanation": "Worshipping under the NICON Bridge, amid traffic and livestock, earned the fellowship the name \"The Under-Bridge Church.\""
              },
              {
                "questionText": "In which year did the church move to Dominion Villa?",
                "type": "mcq",
                "options": [
                  "1989",
                  "1995",
                  "1997",
                  "1999",
                  "2001"
                ],
                "correctAnswer": 3,
                "explanation": "The church moved to its permanent international headquarters at Dominion Villa in November 1999."
              }
            ]
          }
        },
        {
          "title": "Topic 2: The Call to Ministry",
          "type": "text",
          "duration": 600,
          "textContent": "Lesson Content:\n\nIn 1986, God spoke: \"Go raise me a people of power! People who will bear the rule over the kingdom of darkness... teach them the WORD OF FAITH AND DOMINION, so they may walk as kings and priests here on earth.\" This is the call—to raise people, not just build buildings. Archbishop John Praise Daniel left his FCDA job after God rebuked him for dividing his focus. The ministry started like a mustard seed. Today it is a mighty tree.\n\nYour calling matters too. Every believer is called to the ministry of reconciliation. Whether you are a student, a CEO, a parent, or a retiree, your life is a pulpit.\n\nKey Scriptures (KJV & AMP):\n\n\"Go ye therefore, and teach all nations, baptizing them in the name of the Father, and of the Son, and of the Holy Ghost.\" — Matthew 28:19 (KJV)\n\n\"Go therefore and make disciples of all the nations, baptizing them into the name of the Father and of the Son and of the Holy Spirit.\"\n\n— Matthew 28:19 (AMP)\n\n\"But ye are a chosen generation, a royal priesthood, an holy nation, a peculiar people; that ye should shew forth the praises of him who hath called you out of darkness into his marvellous light.\" — 1 Peter 2:9 (KJV)\n\n\"But you are a chosen race, a royal priesthood, a dedicated nation, a people claimed by God for His own, to proclaim the excellencies and wonderful deeds of Him Who called you out of darkness into His marvelous light.\"\n\n— 1 Peter 2:9 (AMP)\n\nPut It Into Practice\n\nWorkgroup Task: With a partner from a different generation, identify three \"secular\" jobs and discuss how each becomes a ministry field.\n\nLife Application Tips:\n\n- - Write down what you believe God is calling you to do.\n- - Ask a mature believer to help you discern your calling.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between salvation and a specific ministry calling?\n2. How did Archbishop John Praise Daniel demonstrate obedience?\n3. Why did God rebuke him for adding \"many other things\" to the assignment?\n4. Can you be called to ministry while working a secular job?\n5. What are the signs of a genuine call from God?\n6. How does the \"ministry of reconciliation\" apply to your life right now?\n7. What sacrifices are you willing to make to follow God's call?\n8. How can you support someone in your church who senses a calling?\n9. What does it mean to be a \"king and priest\" in your current stage of life?\n10. How do you guard against confusing ambition with divine calling?",
          "quiz": {
            "title": "Topic 2 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "The 1986 divine call was to raise a people of power who would walk as:",
                "type": "mcq",
                "options": [
                  "kings and priests",
                  "servants only",
                  "prophets only",
                  "teachers only",
                  "strangers"
                ],
                "correctAnswer": 0,
                "explanation": "God called for a people of power who would walk as kings and priests on the earth."
              },
              {
                "questionText": "God instructed that the people be taught:",
                "type": "mcq",
                "options": [
                  "the Law of Moses",
                  "the Psalms only",
                  "the Word of Faith and Dominion",
                  "church traditions",
                  "prophecy only"
                ],
                "correctAnswer": 2,
                "explanation": "The instruction was to teach the Word of Faith and Dominion."
              },
              {
                "questionText": "Why was John Praise rebuked by God?",
                "type": "mcq",
                "options": [
                  "He preached too little",
                  "He was adding many other things and dividing his focus",
                  "He refused to travel",
                  "He did not tithe",
                  "He built too many branches"
                ],
                "correctAnswer": 1,
                "explanation": "He was rebuked for adding many other things to the assignment and dividing his focus."
              },
              {
                "questionText": "Which job did John Praise leave in obedience to the call?",
                "type": "mcq",
                "options": [
                  "Teaching",
                  "Banking",
                  "FCDA",
                  "NEPA",
                  "NYSC office"
                ],
                "correctAnswer": 2,
                "explanation": "He left his job at the FCDA in obedience to the call to ministry."
              },
              {
                "questionText": "Every believer is called to the ministry of:",
                "type": "mcq",
                "options": [
                  "teaching",
                  "reconciliation",
                  "prophecy",
                  "apostleship",
                  "singing"
                ],
                "correctAnswer": 1,
                "explanation": "Every believer is called to the ministry of reconciliation, whatever their vocation."
              }
            ]
          }
        },
        {
          "title": "Topic 3: The Vision",
          "type": "text",
          "duration": 600,
          "textContent": "Lesson Content:\n\nThe DCIC vision is clear: \"Reaching the total man with the total gospel of our Lord Jesus Christ and getting them saved, teaching the word of faith and dominion, by establishing and discipling believers in the Bible truth.\"\n\nThis means we care about your spirit, soul, and body. We preach salvation, healing, deliverance, prosperity, and discipleship. We do not just convert; we establish. We do not just gather; we disciple. This vision is your compass.\n\nKey Scriptures (KJV & AMP):\n\n\"Where there is no vision, the people perish.\" — Proverbs 29:18 (KJV)\n\n\"Where there is no vision, the people are unrestrained, but happy is he who keeps the law.\" — Proverbs 29:18 (AMP)\n\n\"Write the vision, and make it plain upon tables, that he may run that readeth it.\" — Habakkuk 2:2 (KJV)\n\n\"Write the vision and engrave it so plainly upon tablets that everyone who passes may read as he hastens by.\" — Habakkuk 2:2 (AMP)\n\nPut It Into Practice\n\nWorkgroup Task: Create a vision board illustrating the four pillars of the DCIC vision using symbols and Scriptures.\n\nLife Application Tips:\n\n- Memorize the DCIC vision statement.\n- Write your personal vision statement and align it with the church's.\n\nReflection Questions (for group discussion):\n\n1. What does \"the total man\" mean to you?\n2. How does teaching \"faith and dominion\" differ from teaching religion?\n3. Why is discipleship the capstone of the vision?\n4. How does a clear vision prevent drifting?\n5. How can you contribute to \"reaching the total man\" in your neighborhood?\n6. What does it mean to be \"established\" in Bible truth?\n7. How does this vision shape DCIC's programs?\n8. What happens to a believer without a personal vision?\n9. How can we make the vision plain to a first-time visitor?\n10. Write the DCIC vision in your own words for a 12-year-old.",
          "quiz": {
            "title": "Topic 3 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "The DCIC vision is about reaching the ______ with the total gospel.",
                "type": "mcq",
                "options": [
                  "rich",
                  "total man",
                  "young",
                  "clergy",
                  "aged"
                ],
                "correctAnswer": 1,
                "explanation": "The vision is to reach \"the total man\" -- spirit, soul, and body -- with the total gospel."
              },
              {
                "questionText": "Proverbs 29:18 says where there is no vision:",
                "type": "mcq",
                "options": [
                  "the people perish",
                  "the people rejoice",
                  "the church grows",
                  "offerings increase",
                  "the pastor rests"
                ],
                "correctAnswer": 0,
                "explanation": "Proverbs 29:18 warns that without vision, the people perish."
              },
              {
                "questionText": "Habakkuk 2:2 instructs us to:",
                "type": "mcq",
                "options": [
                  "hide the vision",
                  "write the vision and make it plain",
                  "dream secretly",
                  "keep silent",
                  "build quickly"
                ],
                "correctAnswer": 1,
                "explanation": "Habakkuk 2:2 instructs believers to write the vision and make it plain."
              },
              {
                "questionText": "DCIC does not just convert people; the church also:",
                "type": "mcq",
                "options": [
                  "charges fees",
                  "establishes them",
                  "registers them",
                  "dismisses them",
                  "celebrates them"
                ],
                "correctAnswer": 1,
                "explanation": "The vision emphasizes establishing and discipling believers, not just conversion."
              },
              {
                "questionText": "The vision is described as your:",
                "type": "mcq",
                "options": [
                  "burden",
                  "compass",
                  "certificate",
                  "salary",
                  "hobby"
                ],
                "correctAnswer": 1,
                "explanation": "The vision is described as a compass that keeps a believer from drifting."
              }
            ]
          }
        },
        {
          "title": "Topic 4: The Dominion Family",
          "type": "text",
          "duration": 600,
          "textContent": "Lesson Content:\n\nDCIC is not a social club. It is a covenant family. We gather for quality praise and worship, we are equipped with the Word for growth, we fellowship in covenant relationship, and we make a formidable impact for God's kingdom. Here, you are not a number. You are family.\n\nKey Scriptures (KJV & AMP):\n\n\"And they continued stedfastly in the apostles' doctrine and fellowship, and in breaking of bread, and in prayers.\" — Acts 2:42 (KJV)\n\n\"And they steadfastly persevered, devoting themselves constantly to the instruction and fellowship of the apostles, to the breaking of bread, and to prayers.\" — Acts 2:42 (AMP)\n\n\"For as the body is one, and hath many members, and all the members of that one body, being many, are one body: so also is Christ.\"\n\n— 1 Corinthians 12:12 (KJV)\n\n\"For just as the body is one and yet has many parts, and all the parts, though many, form one body, so it is with Christ.\" — 1 Corinthians 12:12 (AMP)\n\nPut It Into Practice\n\nWorkgroup Task: Draw a \"family tree\" of ministry departments. Place yourself on it and explain how you give and receive nourishment.\n\nLife Application Tips:\n\n- Introduce yourself to someone older or younger than you after service.\n- Join a small group or cell fellowship this month.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between attendance and covenant family membership?\n2. How does quality worship set the family atmosphere?\n3. Why is fellowship called a \"covenant relationship\"?\n4. What does it mean to be \"equipped with the Word\"?\n5. How can older members mentor younger ones?\n6. How can younger members honor older ones?\n7. What are the dangers of isolation?\n8. How does the Dominion Family impact Abuja and beyond?\n9. What practical steps transition you from visitor to family?\n10. Describe a time when the church family supported you.",
          "quiz": {
            "title": "Topic 4 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "Acts 2:42 -- the early church continued steadfastly in the apostles' doctrine and:",
                "type": "mcq",
                "options": [
                  "buying and selling",
                  "fasting only",
                  "temple rituals",
                  "circumcision",
                  "fellowship, breaking of bread, and prayers"
                ],
                "correctAnswer": 4,
                "explanation": "Acts 2:42 lists doctrine, fellowship, the breaking of bread, and prayers as marks of the early church."
              },
              {
                "questionText": "1 Corinthians 12:12 compares the church to:",
                "type": "mcq",
                "options": [
                  "a tree",
                  "a river",
                  "a body with many members",
                  "a house",
                  "an army"
                ],
                "correctAnswer": 2,
                "explanation": "1 Corinthians 12:12 compares the church to one body made up of many members."
              },
              {
                "questionText": "The lesson describes DCIC as a:",
                "type": "mcq",
                "options": [
                  "social club",
                  "business enterprise",
                  "covenant family",
                  "school",
                  "charity organization"
                ],
                "correctAnswer": 2,
                "explanation": "DCIC is described as a covenant family, not a social club."
              },
              {
                "questionText": "In the Dominion Family, you are not a number; you are:",
                "type": "mcq",
                "options": [
                  "a visitor",
                  "family",
                  "a customer",
                  "a worker",
                  "a guest"
                ],
                "correctAnswer": 1,
                "explanation": "The lesson emphasizes that every member is family, not a number."
              },
              {
                "questionText": "What happens to a believer who isolates himself?",
                "type": "mcq",
                "options": [
                  "He becomes famous",
                  "He grows faster",
                  "No one grows in isolation",
                  "He becomes richer",
                  "He becomes stronger"
                ],
                "correctAnswer": 2,
                "explanation": "The lesson teaches that no one grows spiritually in isolation."
              }
            ]
          }
        },
        {
          "title": "Topic 5: The Dominion Mandate",
          "type": "text",
          "duration": 600,
          "textContent": "Lesson Content:\n\nIn 1986, God said: \"My people have walked too long as nonentities and second-class citizens. Go, teach them the WORD OF FAITH AND DOMINION, so they may walk as kings and priests here on earth, being absolutely in charge and in control of all situations in their lives.\"\n\nYou were created for dominion (Gen. 1:26). You were redeemed for dominion (Rev. 5:10). You are not a victim. You are a victor. This is the Dominion Mandate.\n\nKey Scriptures (KJV & AMP):\n\n\"And God said, Let us make man in our image, after our likeness: and let them have dominion over the fish of the sea, and over the fowl of the air, and over the cattle, and over all the earth, and over every creeping thing that creepeth upon the earth.\" — Genesis 1:26 (KJV)\n\n\"God said, Let Us make mankind in Our image, after Our likeness, and let them have complete authority over the fish of the sea, the birds of the air, the beasts, and over all of the earth.\" — Genesis 1:26 (AMP)\n\n\"And hast made us unto our God kings and priests:\n\nMatthew 13:31-32 (KJV)\n\n\"Another parable put he forth unto them, saying, The kingdom of heaven is like to a grain of mustard seed, which a man took, and sowed in his field: Which indeed is the least of all seeds: but when it is grown, it is the greatest among herbs, and becometh a tree, so that the birds of the air come and lodge in the branches thereof.\"\n\nMatthew 13:31-32 (AMP)\n\n\"He gave them another parable, saying, 'The kingdom of heaven is like a grain of mustard seed, which a man took and sowed in his field; and of all the seeds it is the smallest, but when it has grown it is the largest of the garden herbs and becomes a tree, so that the birds of the air find shelter in its branches.'\"\n\nZechariah 4:10 (KJV)\n\n\"For who hath despised the day of small things?\"\n\nZechariah 4:10 (AMP)\n\n\"Who is there who dares to despise the day of small things?\"\n\n1 Corinthians 3:6-7 (KJV)\n\n\"I have planted, Apollos watered; but God gave the increase. So then neither is he that planteth any thing, neither he that watereth; but God that giveth the increase.\"\n\n1 Corinthians 3:6-7 (AMP)\n\n\"I planted, Apollos watered, but God was causing the growth. So neither is the one who plants nor the one who waters anything, but only God who causes the growth.\"\n\nPut It Into Practice\n\nWorkgroup Task: The Legacy Timeline: In groups of 4-6, create a visual timeline of DCIC's journey from 1986 to the present. Include key milestones, challenges, and breakthroughs. Each group should present one lesson from the timeline that applies to their own spiritual walk. Allow 15 minutes for preparation and 3 minutes per group for presentation.\n\nLife Application Tips:\n\n- Tip 1 : Honor the Past: Take time this week to speak with an older member of the church and ask them about their earliest memories of DCIC. Write down what you learn.\n- Tip 2 — Sow Your Seed: Identify one small act of faithfulness you can perform this week that may seem insignificant now but could yield great fruit in the future.\n- Tip 3 — Global Mindset: Pray specifically for one international DCIC branch each day this week.\n\nReflection Questions (for group discussion):\n\n1. Why do you think God often chooses small beginnings for great destinies?\n2. What can we learn from Pastor John Praise's obedience to God's vision despite limited resources?\n3. How does the \"under-bridge church\" story challenge your perspective on worship and sacrifice?\n4. In what ways has DCIC's growth from Bwari to the nations encouraged your personal faith journey?\n5. What role did the members play in building the first church structure, and what does this teach us about ownership?\n6. How can you contribute to the next phase of growth in your local branch, regardless of your age?\n7. What does the global expansion of DCIC reveal about the universal nature of the Gospel?\n8. Why is it important to document and celebrate church history for younger generations?\n9. How does the story of DCIC inspire you to remain faithful in your current season of life?\n10. What \"mustard seed\" has God placed in your hands that you need to sow today?",
          "quiz": {
            "title": "Topic 5 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "Genesis 1:26 -- man was given dominion over:",
                "type": "mcq",
                "options": [
                  "other men only",
                  "angels",
                  "nothing at all",
                  "the seas only",
                  "the fish, fowl, cattle, and all the earth"
                ],
                "correctAnswer": 4,
                "explanation": "Genesis 1:26 gives mankind dominion over the fish, the fowl, the cattle, and all the earth."
              },
              {
                "questionText": "Revelation 5:10 -- He has made us unto our God:",
                "type": "mcq",
                "options": [
                  "servants",
                  "kings and priests",
                  "soldiers",
                  "strangers",
                  "tenants"
                ],
                "correctAnswer": 1,
                "explanation": "Revelation 5:10 says believers are made kings and priests unto God."
              },
              {
                "questionText": "According to the Dominion Mandate, a believer is not a victim but a:",
                "type": "mcq",
                "options": [
                  "spectator",
                  "victor",
                  "slave",
                  "foreigner",
                  "beggar"
                ],
                "correctAnswer": 1,
                "explanation": "The Dominion Mandate teaches that a believer is a victor, not a victim."
              },
              {
                "questionText": "The parable of the mustard seed is found in:",
                "type": "mcq",
                "options": [
                  "John 3:16",
                  "Psalm 23",
                  "Matthew 13:31-32",
                  "Exodus 20",
                  "Acts 2:1-4"
                ],
                "correctAnswer": 2,
                "explanation": "The parable of the mustard seed, illustrating small beginnings, is recorded in Matthew 13:31-32."
              },
              {
                "questionText": "\"For who hath despised the day of small things?\" is found in:",
                "type": "mcq",
                "options": [
                  "Zechariah 4:10",
                  "Isaiah 53:5",
                  "Romans 8:1",
                  "Genesis 1:1",
                  "Matthew 6:33"
                ],
                "correctAnswer": 0,
                "explanation": "This line comes from Zechariah 4:10, a key scripture on small beginnings."
              }
            ]
          }
        }
      ]
    },
    {
      "title": "The Status of a Believer",
      "lectures": [
        {
          "title": "Topic 6: Justification by Faith",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nJustification is the divine verdict that transforms a condemned sinner into a righteous saint— not because of what you did, but because of what Christ finished.\n\nCore Teaching:\n\nTo be justified means to be declared righteous, to be free from guilt, and to stand before God without condemnation. Our justification is not earned by religious effort, moral achievement, or cultural status. It is received by faith in the finished work of Jesus Christ (Romans 5:1).\n\nWhen you believe that Jesus is Lord, that God raised Him from the dead, and you confess this with your mouth (Romans 10:9-10), something eternal happens: you are redeemed, you are forgiven, you are accepted, and you are positioned in heavenly places. This is not a feeling; it is a fact established by God's Word.\n\nThe components of justification include:\n\n- Redemption: You are set free from sin and have become God's property (Ephesians 1:7; 1 Peter 1:18-19).\n\n- No Condemnation: The penalty for your past, present, and future sins has been fully paid (Romans 8:1).\n\n- Authority: You are seated with Christ in heavenly places, far above principalities and powers (Ephesians 2:6; 1:17-20).\n\n- Sonship: You are no longer a slave to sin; you are a child of God and a joint-heir with Christ (John 1:12; Romans 8:17; Galatians 4:7).\n\nYour assurance rests on three pillars: FACT (the Word of God), FAITH (your response to the Word), and FEELING (the emotional result of faith). Do not wait for a feeling to confirm your salvation. If you have believed and confessed, you are saved. The joy will follow.\n\nKey Scriptures:\n\nRomans 5:1 (KJV): \"Therefore, being justified by faith, we have peace with God through our Lord Jesus Christ.\"\n\nRomans 5:1 (AMP): \"Therefore, since we have been justified, that is, acquitted of sin, declared blameless before God by faith, we have peace with God through our Lord Jesus Christ.\"\n\nRomans 10:9-10 (KJV): \"That if thou shalt confess with thy mouth the Lord Jesus, and shalt believe in thine heart that God hath raised him from the dead, thou shalt be saved. For with the heart man believeth unto righteousness; and with the mouth confession is made unto salvation.\"\n\nRomans 10:9-10 (AMP): \"If you declare with your mouth that Jesus is Lord, and believe in your heart that God raised Him from the dead, you will be saved. For with the heart a person believes, resulting in righteousness, and with the mouth he confesses, resulting in salvation.\"\n\nEphesians 2:8-9 (KJV): \"For by grace are ye saved through faith; and that not of yourselves: it is the gift of God: Not of works, lest any man should boast.\"\n\nEphesians 2:8-9 (AMP): \"For it is by grace you have been saved through faith. And this is not of yourselves, it is the gift of God; not as a result of works, so that no one may boast.\"\n\nPut It Into Practice\n\nWorkgroup Task: The Assurance Workshop: In groups of 4, create a \"Salvation Assurance Guide\"—a one-page handout that a new believer could use when doubts arise. Include three FACT scriptures, three FAITH declarations, and three common feelings that should not determine one's standing with God. Share your guides with the class.\n\n- Tip 1 — Fact Check: When doubt arises, open your Bible to Romans 8:1 and read it aloud ten times. Let the fact of God's Word silence the lie of condemnation.\n\n- Tip 2 — Confession Calendar: Write Romans 10:9-10 on a card and place it on your mirror. Confess it every morning for 30 days.\n\n- Tip 3 — Mentor a New Believer: If you have been saved for more than a year, find someone who recently gave their life to Christ and walk them through the truths of justification.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between justification and forgiveness?\n2. Why is it impossible to be justified by human effort or good works?\n3. How does understanding redemption as \"being purchased by God\" change how you view your body, time, and resources?\n4. What does \"no condemnation\" mean for a believer who still struggles with sinful thoughts or habits?\n5. Why is it dangerous to base your salvation on feelings rather than on the fact of God's Word?\n6. How does the doctrine of justification by faith promote humility rather than pride?\n7. In what ways do cultural or family traditions sometimes contradict the truth of justification by faith?\n8. What does it mean to be \"seated with Christ in heavenly places,\" and how should this affect your daily decisions?\n9. If you are a joint-heir with Christ, what inheritance do you have access to?\n10. How would you explain justification by faith to a 12-year-old who thinks they must be \"good enough\" for God to love them?",
          "quiz": {
            "title": "Topic 6 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "To be justified means to be:",
                "type": "mcq",
                "options": [
                  "made religious",
                  "declared righteous and free from guilt",
                  "baptized in water",
                  "registered in church",
                  "ordained as a pastor"
                ],
                "correctAnswer": 1,
                "explanation": "Justification means being declared righteous and free from guilt before God."
              },
              {
                "questionText": "We are justified by:",
                "type": "mcq",
                "options": [
                  "good works",
                  "church attendance",
                  "our offerings",
                  "our culture and status",
                  "faith in the finished work of Jesus Christ"
                ],
                "correctAnswer": 4,
                "explanation": "Justification is received by faith in the finished work of Jesus Christ (Romans 5:1), not by effort or status."
              },
              {
                "questionText": "Romans 10:9-10 -- salvation requires believing in the heart and:",
                "type": "mcq",
                "options": [
                  "tithing",
                  "confessing with the mouth",
                  "fasting forty days",
                  "singing hymns",
                  "traveling to Jerusalem"
                ],
                "correctAnswer": 1,
                "explanation": "Romans 10:9-10 pairs believing in the heart with confessing with the mouth."
              },
              {
                "questionText": "\"There is therefore now no condemnation\" is:",
                "type": "mcq",
                "options": [
                  "Romans 3:23",
                  "Romans 8:1",
                  "Romans 5:8",
                  "Romans 12:1",
                  "Romans 1:16"
                ],
                "correctAnswer": 1,
                "explanation": "This assurance is found in Romans 8:1."
              },
              {
                "questionText": "The three pillars of assurance are Fact, Faith, and:",
                "type": "mcq",
                "options": [
                  "Friends",
                  "Funds",
                  "Fame",
                  "Fasting",
                  "Feelings"
                ],
                "correctAnswer": 4,
                "explanation": "Assurance rests on Fact (God's Word), Faith (your response), and Feeling (the result) -- in that order."
              }
            ]
          }
        },
        {
          "title": "Topic 7: Your New Identity in Christ",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nYou are not an upgraded version of your old self; you are an entirely new creation. The moment you received Christ, your identity was permanently rewritten in heaven's registry.\n\nCore Teaching:\n\nMany believers live defeated lives because they have never fully grasped who they are in Christ. James 1:23-25 compares the Word of God to a mirror. When you look into it, you must not forget what kind of person you are. You are a new creature (2 Corinthians 5:17). The old has passed away; behold, all things have become new.\n\nThe Components of Man:\n\nMan is a triune being—spirit, soul, and body (1 Thessalonians 5:23).\n\n- The Spirit: The real you. It is your spirit that was born again and now houses the Holy Spirit. Your spirit is as righteous as Jesus is righteous (1 John 4:17).\n\n- The Soul: Comprised of your mind, will, and emotions. This is where the battle for transformation takes place. As your mind is renewed by the Word, your body and circumstances follow.\n\n- The Body: The temporary house of your spirit. It is to be honored as God's temple, not abused or neglected.\n\nRighteousness: To be righteous means to have a right standing with God. It is the ability to stand before God without sin consciousness (Romans 8:1; 1 Corinthians 1:30). Sin no longer has dominion over you (Romans 6:14). Righteousness is not achieved by performance; it is received by faith (Romans 4:5).\n\nGod's Temple: Your body is the dwelling place of the Almighty (1 Corinthians 3:16; 6:19-20). God does not live in buildings made by human hands; He lives in you. This truth should govern how you eat, dress, speak, and relate to others.\n\nKey Scriptures:\n\n2 Corinthians 5:17 (KJV): \"Therefore if any man be in Christ, he is a new creature: old things are passed away; behold, all things are become new.\"\n\n2 Corinthians 5:17 (AMP): \"Therefore if anyone is in Christ, he is a new creature; the old things have passed away; behold, new things have come.\"\n\n1 Thessalonians 5:23 (KJV): \"And the very God of peace sanctify you wholly; and I pray God your whole spirit and soul and body be preserved blameless unto the coming of our Lord Jesus Christ.\"\n\n1 Thessalonians 5:23 (AMP): \"Now may the God of peace Himself sanctify you through and through; and may your spirit and soul and body be kept complete and blameless at the coming of our Lord Jesus Christ.\"\n\n1 Corinthians 6:19-20 (KJV): \"What? know ye not that your body is the temple of the Holy Ghost which is in you, which ye have of God, and ye are not your own? For ye are bought with a price: therefore glorify God in your body, and in your spirit, which are God's.\"\n\n1 Corinthians 6:19-20 (AMP): \"Do you not know that your body is a temple of the Holy Spirit who is within you, whom you have from God, and that you are not your own? For you have been bought with a price: therefore glorify God in your body.\"\n\nPut It Into Practice\n\nWorkgroup Task: Identity Collage: In mixed-age groups, create a visual collage (using magazines, printouts, or drawings) that represents the \"New Identity in Christ.\" Include images or words for: New Creature, Righteous, Temple, Royal Priesthood, and More Than a Conqueror. Each group explains one image to the class.\n\nLife Application Tips:\n\n- Tip 1 — Mirror Ministry: For the next 7 days, look in the mirror each morning and declare: \"I am a new creation. Old things have passed away. I am the righteousness of God in Christ.\"\n- Tip 2 — Temple Check: Do a one-week audit of what you put into your body (food, media, conversations) and ask: \"Does this honor the Holy Spirit's residence?\"\n- Tip 3 — Mind Renewal: Replace one negative self-talk statement with a corresponding Scripture each day this week.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between behavior modification and being a \"new creature\"?\n2. Why is it important to distinguish between your spirit, soul, and body?\n3. How does knowing your spirit is already perfect change how you handle mistakes?\n4. What are some \"old things\" you need to let pass away so you can embrace your new identity?\n5. How does the concept of your body as God's temple affect your lifestyle choices?\n6. Why do many believers still struggle with \"sin consciousness\" even after being saved?\n7. How can you renew your mind daily to align with your new identity?\n8. What does it mean to \"glorify God in your body\" in a culture that promotes self-indulgence?\n9. How should a teenager view their identity differently from their unsaved classmates?\n10. Write a list of 10 \"I am\" statements based on Scripture that describe your new identity.",
          "quiz": {
            "title": "Topic 7 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "2 Corinthians 5:17 -- anyone in Christ is a:",
                "type": "mcq",
                "options": [
                  "better sinner",
                  "visitor",
                  "servant",
                  "new creature",
                  "upgraded man"
                ],
                "correctAnswer": 3,
                "explanation": "2 Corinthians 5:17 declares that anyone in Christ is a new creature; old things have passed away."
              },
              {
                "questionText": "Man is a triune being: spirit, soul, and:",
                "type": "mcq",
                "options": [
                  "mind",
                  "heart",
                  "body",
                  "will",
                  "strength"
                ],
                "correctAnswer": 2,
                "explanation": "1 Thessalonians 5:23 describes man as spirit, soul, and body."
              },
              {
                "questionText": "Which part of man was born again at salvation?",
                "type": "mcq",
                "options": [
                  "The body",
                  "The soul",
                  "The flesh",
                  "The spirit",
                  "The mind"
                ],
                "correctAnswer": 3,
                "explanation": "It is the human spirit that is born again and now houses the Holy Spirit."
              },
              {
                "questionText": "1 Corinthians 6:19-20 -- your body is:",
                "type": "mcq",
                "options": [
                  "yours alone",
                  "the temple of the Holy Spirit",
                  "irrelevant to God",
                  "cursed",
                  "merely temporary"
                ],
                "correctAnswer": 1,
                "explanation": "1 Corinthians 6:19-20 calls the believer's body the temple of the Holy Spirit."
              },
              {
                "questionText": "The soul is comprised of the mind, will, and:",
                "type": "mcq",
                "options": [
                  "strength",
                  "body",
                  "spirit",
                  "heart",
                  "emotions"
                ],
                "correctAnswer": 4,
                "explanation": "The soul is made up of the mind, will, and emotions -- the seat of the believer's ongoing transformation."
              }
            ]
          }
        },
        {
          "title": "Topic 8: Growing in Grace",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nSalvation is a birth; growth is a journey. Grace is not merely how you started the race—it is the divine enablement that keeps you running until you see His face.\n\nCore Teaching:\n\nOur position in Christ is one of grace (Romans 5:2). Grace is unmerited favor—the undeserved, unearned, and unrepayable love of God toward us. But grace is also a force. It is the divine empowerment that enables us to live the Christian life successfully. Like a baby needs milk to grow, every believer needs the Word of God to develop spiritually (1 Peter 2:2).\n\nPrayer: Prayer is communication with God. It is to the spirit what air is to the body. God answers prayer (Matthew 7:7-11; Mark 11:24). Effective prayer is:\n\n- Directed to the Father (John 16:23-27)\n\n- Made in faith (Mark 11:24)\n\n- Offered in Jesus' Name (John 16:23-27)\n\n- Based on the Word (Isaiah 43:25-26)\n\n- Dependent on the Holy Spirit (Romans 8:26-27)\n\nThe Word of God: As milk is to a baby, so is the Word to the believer (1 Peter 2:2; Deuteronomy 8:3; Joshua 1:8). The Word is food for your spirit.\n\nQuiet Time: The most important habit for spiritual growth is a daily quiet time—Bible reading and prayer. To achieve this: get a Bible you understand; set a specific time (mornings are ideal); find a quiet place; follow a reading plan; expect to hear from God; ask the Holy Spirit to teach you; and never leave home spiritually naked.\n\nFellowship: No one grows in isolation. We need the right atmosphere and the right people (Psalm 92:13-14; 1 Corinthians 15:33).\n\nWitnessing: One of the fastest ways to become established is to testify to what God is doing in your life (Revelation 12:11; 2 Corinthians 5:18; Mark 16:15). You are a minister of reconciliation.\n\nKey Scriptures:\n\n2 Peter 3:18 (KJV): \"But grow in grace, and in the knowledge of our Lord and Saviour Jesus Christ. To him be glory both now and for ever. Amen.\"\n\n2 Peter 3:18 (AMP): \"But grow in the grace and knowledge of our Lord and Savior Jesus Christ. To Him be the glory, both now and to the day of eternity. Amen.\"\n\n1 Peter 2:2 (KJV): \"As newborn babes, desire the sincere milk of the word, that ye may grow thereby.\"\n\n1 Peter 2:2 (AMP): \"Like newborn babies, long for the pure milk of the word, so that by it you may grow in respect to salvation.\"\n\nJoshua 1:8 (KJV): \"This book of the law shall not depart out of thy mouth; but thou shalt meditate therein day and night, that thou mayest observe to do according to all that is written therein: for then thou shalt make thy way prosperous, and then thou shalt have good success.\"\n\nJoshua 1:8 (AMP): \"This Book of the Law shall not depart from your mouth, but you shall read and meditate on it day and night, so that you may be careful to do everything in accordance with all that is written in it; for then you will make your way prosperous, and then you will be successful.\"\n\nPut It Into Practice\n\nWorkgroup Task: The Growth Challenge: In groups, design a \"30-Day Spiritual Growth Tracker\" that includes daily prayer, Bible reading, fellowship, and witnessing goals. Make it visually appealing and practical for both teenagers and working adults. The best design will be duplicated for the entire class.\n\nLife Application Tips:\n\n- Tip 1 — Morning Alarm: Set your alarm 15 minutes earlier this week. Use that time exclusively for Bible reading and prayer before checking your phone.\n- Tip 2 — Accountability Partner: Pair up with someone of a different generation. Text each other daily to confirm you have completed your quiet time.\n- Tip 3 — Witnessing Goal: Share your faith with at least one person this week. Write down what happened and how it affected your own faith.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between grace as \"unmerited favor\" and grace as \"divine empowerment\"?\n2. Why is prayer described as \"air to the spirit-man\"? What happens to a believer who stops praying?\n3. What are the five ingredients of effective prayer, and which one do you need to strengthen most?\n4. How does a daily quiet time differ from a religious routine?\n5. Why is early morning generally considered the best time for quiet time?\n6. What does it mean to be \"spiritually naked,\" and how can you avoid leaving home that way?\n7. How does your choice of friends and environment affect your spiritual growth?\n8. Why is witnessing considered a pathway to personal spiritual establishment?\n9. What is the connection between meditation on God's Word and material prosperity?\n10. Create a personal growth plan for the next 30 days that includes prayer, Word intake, fellowship, and witnessing.",
          "quiz": {
            "title": "Topic 8 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "1 Peter 2:2 -- newborn babes should desire the sincere milk of:",
                "type": "mcq",
                "options": [
                  "the word",
                  "water",
                  "honey",
                  "solid meat",
                  "wine"
                ],
                "correctAnswer": 0,
                "explanation": "1 Peter 2:2 calls believers to desire the sincere milk of the word in order to grow."
              },
              {
                "questionText": "Prayer is to the spirit what ______ is to the body:",
                "type": "mcq",
                "options": [
                  "food",
                  "sleep",
                  "air",
                  "water",
                  "exercise"
                ],
                "correctAnswer": 2,
                "explanation": "Prayer is described as being to the spirit what air is to the body."
              },
              {
                "questionText": "Which is NOT an ingredient of effective prayer?",
                "type": "mcq",
                "options": [
                  "Directed to the Father",
                  "Made in faith",
                  "In Jesus' name",
                  "Offered for public display",
                  "Dependent on the Holy Spirit"
                ],
                "correctAnswer": 3,
                "explanation": "Effective prayer is directed to the Father, made in faith, in Jesus' name, and by the Spirit -- not offered for public display."
              },
              {
                "questionText": "The most important habit for spiritual growth is:",
                "type": "mcq",
                "options": [
                  "daily quiet time (Bible reading and prayer)",
                  "weekly fasting",
                  "night vigils",
                  "choir practice",
                  "daily evangelism"
                ],
                "correctAnswer": 0,
                "explanation": "A daily quiet time of Bible reading and prayer is named as the single most important growth habit."
              },
              {
                "questionText": "2 Peter 3:18 -- grow in grace and in the knowledge of:",
                "type": "mcq",
                "options": [
                  "angels",
                  "the law",
                  "the prophets",
                  "our Lord and Saviour Jesus Christ",
                  "the world"
                ],
                "correctAnswer": 3,
                "explanation": "2 Peter 3:18 calls believers to grow in grace and in the knowledge of our Lord and Saviour Jesus Christ."
              }
            ]
          }
        }
      ]
    },
    {
      "title": "Understanding Scriptural Tenets",
      "lectures": [
        {
          "title": "Topic 9: Holy Communion",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nHoly Communion is not a religious ritual; it is a sacred covenant meal that connects you to the life, power, and victory of the resurrected Christ.\n\nCore Teaching:\n\nHoly Communion, also called the Eucharist or the Lord's Supper, was instituted by Jesus on the night before His crucifixion (Matthew 26:26-28; 1 Corinthians 11:23-26). As believers, we partake of the bread and the cup in remembrance of Christ's broken body and shed blood. But communion is more than remembrance—it is participation. When we eat the bread and drink the cup, we are proclaiming the Lord's death until He comes, and we are partaking of the benefits of His sacrifice: healing, deliverance, forgiveness, and life.\n\nThe bread represents His body, which was broken for our physical healing (Isaiah 53:5; 1 Peter 2:24). The cup represents His blood, which was shed for the remission of sins and the establishment of the New Covenant (Matthew 26:28). Communion should be received with reverence, self-examination, and faith—not with fear, but with joyful expectation of encountering Christ.\n\nFor the young believer, communion is your declaration that you belong to Jesus. For the mature saint, it is a refreshing of covenant and a realignment with kingdom purpose. For the elder, it is a foretaste of the marriage supper of the Lamb.\n\nKey Scriptures:\n\n1 Corinthians 11:23-26 (KJV): \"For I have received of the Lord that which also I delivered unto you, That the Lord Jesus the same night in which he was betrayed took bread: And when he had given thanks, he brake it, and said, Take, eat: this is my body, which is broken for you: this do in remembrance of me. After the same manner also he took the cup, when he had supped, saying, This cup is the new testament in my blood: this do ye, as oft as ye drink it, in remembrance of me. For as often as ye eat this bread, and drink this cup, ye do shew the Lord's death till he come.\"\n\n1 Corinthians 11:23-26 (AMP): \"For I received from the Lord that which I also passed on to you, that the Lord Jesus on the night in which He was betrayed took bread; and when He had given thanks, He broke it and said, 'This is My body, which is for you; do this in remembrance of Me.' In the same way, after supper He took the cup, saying, 'This cup is the new covenant in My blood; do this, as often as you drink it, in remembrance of Me.' \"For as often as you eat this bread and drink this cup, you proclaim the Lord's death until He comes again.\"\n\nJohn 6:53-56 (KJV): \"Then Jesus said unto them, Verily, verily, I say unto you, Except ye eat the flesh of the Son of man, and drink his blood, ye have no life in you.\" Whosoever eateth my flesh, and drinketh my blood, hath eternal life; and I will raise him up at the last day. For my flesh is meat indeed, and my blood is drink indeed. He that eateth my flesh, and drinketh my blood, dwelleth in me, and I in him.\"\n\nJohn 6:53-56 (AMP): \"So Jesus said to them, 'Truly, truly, I say to you, unless you eat the flesh of the Son of Man and drink His blood, you have no life in yourselves. He who eats My flesh and drinks My blood has eternal life, and I will raise him up on the last day. For My flesh is true food, and My blood is true drink. He who eats My flesh and drinks My blood remains in Me, and I in him.'\"\n\n1 Corinthians 10:16 (KJV): \"The cup of blessing which we bless, is it not the communion of the blood of Christ? The bread which we break, is it not the communion of the body of Christ?\"\n\n1 Corinthians 10:16 (AMP): \"Is not the cup of blessing which we bless a sharing in the blood of Christ? Is not the bread which we break a sharing in the body of Christ?\"\n\nPut It Into Practice\n\nWorkgroup Task: Communion Meditation: In groups, write a 5-minute guided meditation for Holy Communion that could be used during a church service. It should include: (1) A moment of reflection on Christ's sacrifice, (2) A scripture declaration, (3) A prayer of thanksgiving, and (4) A proclamation of victory. One person from each group leads the class through their meditation.\n\nLife Application Tips:\n\n- Tip 1 — Personal Communion: If you are unable to attend church, set aside time at home to take communion with reverence, using bread and juice, while meditating on 1 Corinthians 11:23-26.\n- Tip 2 — Healing Focus: The next time you take communion, specifically meditate on Isaiah 53:5 and receive healing for any area of your body that needs it.\n- Tip 3 — Family Altar: If you have family at home, lead a brief communion service together once a month, explaining the meaning to children.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between viewing Holy Communion as a ritual versus a covenant meal?\n2. Why did Jesus choose bread and wine as the symbols of His body and blood?\n3. What does it mean to \"examine yourself\" before taking communion (1 Corinthians 11:28)?\n4. How does communion connect to the believer's physical healing?\n5. What is the significance of the phrase \"the new testament in my blood\"?\n6. How often should a believer partake of Holy Communion, and why?\n7. What does it mean to \"proclaim the Lord's death until He comes\" through communion?\n8. How can parents teach the meaning of communion to their children?\n9. What should a believer do if they feel unworthy to take communion?\n10. Describe a time when taking communion strengthened your faith or brought you healing.",
          "quiz": {
            "title": "Topic 9 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "Communion was instituted by Jesus on:",
                "type": "mcq",
                "options": [
                  "Easter morning",
                  "the Sabbath day",
                  "the night in which He was betrayed",
                  "the Day of Pentecost",
                  "the day of Ascension"
                ],
                "correctAnswer": 2,
                "explanation": "1 Corinthians 11:23 records that communion was instituted on the night Jesus was betrayed."
              },
              {
                "questionText": "The bread represents:",
                "type": "mcq",
                "options": [
                  "His shed blood",
                  "His broken body",
                  "the church",
                  "the law of Moses",
                  "the manna in the wilderness"
                ],
                "correctAnswer": 1,
                "explanation": "The bread represents Christ's body, broken for our healing (Isaiah 53:5; 1 Peter 2:24)."
              },
              {
                "questionText": "The cup represents:",
                "type": "mcq",
                "options": [
                  "water baptism",
                  "the law of Moses",
                  "tithes and offerings",
                  "the fire of the Spirit",
                  "the new covenant in His blood"
                ],
                "correctAnswer": 4,
                "explanation": "The cup represents the new covenant established in Christ's blood (Matthew 26:28)."
              },
              {
                "questionText": "\"Examine yourself\" before communion is found in 1 Corinthians:",
                "type": "mcq",
                "options": [
                  "10:16",
                  "11:28",
                  "12:1",
                  "13:1",
                  "5:7"
                ],
                "correctAnswer": 1,
                "explanation": "The instruction to examine yourself before communion is 1 Corinthians 11:28."
              },
              {
                "questionText": "1 Corinthians 10:16 -- communion is a sharing/participation in:",
                "type": "mcq",
                "options": [
                  "the blood and body of Christ",
                  "the Jewish feasts",
                  "the manna",
                  "the Passover lamb only",
                  "Old Testament sacrifices"
                ],
                "correctAnswer": 0,
                "explanation": "1 Corinthians 10:16 calls communion a sharing in the blood and body of Christ."
              }
            ]
          }
        },
        {
          "title": "Topic 10: Water Baptism",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thought:\n\nWater baptism is your public declaration of a private transformation. It is the funeral of your old life and the resurrection announcement of your new one.\n\nCore Teaching:\n\nWater baptism is an outward act of an inward reality. It is not optional for the believer; it is a command of Christ (Matthew 28:19). When you are baptized in water, you are identifying with the death, burial, and resurrection of Jesus Christ (Romans 6:3-4). As you go down into the water, your old sinful nature is symbolically buried with Christ. As you come up out of the water, you are raised to walk in newness of life.\n\nBaptism is an act of obedience that releases grace. It is a spiritual circumcision (Colossians 2:11-12) that cuts away the old life. It is also a prophetic act—what happens in the water often manifests in the spirit. Many believers experience breakthroughs, deliverance, and fresh empowerment after water baptism.\n\nWater baptism is for those who have consciously believed in Jesus Christ. Infant baptism, while meaningful to some traditions, does not align with the biblical pattern of believing first and then being baptized (Acts 8:36-38). The candidate should understand what they are doing and why.\n\nFor the teenager, baptism is a powerful statement to peers that you have chosen Christ. For the adult, it is a line drawn in the sand that the old life is over. For the elder who may have delayed, it is never too late to obey.\n\nKey Scriptures:\n\nRomans 6:3-4 (KJV): \"Know ye not, that so many of us as were baptized into Jesus Christ were baptized into his death? Therefore we are buried with him by baptism into death: that like as Christ was raised up from the dead by the glory of the Father, even so we also should walk in newness of life.\"\n\nRomans 6:3-4 (AMP): \"Or are you ignorant of the fact that all of us who have been baptized into Christ Jesus were baptized into His death? We have therefore been buried with Him through baptism into death, so that just as Christ was raised from the dead through the glory of the Father, we too might walk in newness of life.\"\n\nMatthew 28:19 (KJV): \"Go ye therefore, and teach all nations, baptizing them in the name of the Father, and of the Son, and of the Holy Ghost.\"\n\nMatthew 28:19 (AMP): \"Go therefore and make disciples of all the nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit.\"\n\nActs 2:38 (KJV): \"Then Peter said unto them, Repent, and be baptized every one of you in the name of Jesus Christ for the remission of sins, and ye shall receive the gift of the Holy Ghost.\"\n\nActs 2:38 (AMP): \"And Peter said to them, 'Repent, and each of you be baptized in the name of Jesus Christ for the forgiveness of your sins; and you will receive the gift of the Holy Spirit.'\"\n\nPut It Into Practice\n\nWorkgroup Task: Baptism Testimony Theatre: In groups, prepare a 3-minute skit or spoken-word piece that illustrates the transformation of a life before and after water baptism. Include the symbolism of death, burial, and resurrection. Each group performs for the class.\n\nLife Application Tips:\n\n- Tip 1 — Register for Baptism: If you have believed in Christ but have not yet been baptized, register for the next baptismal service at DCIC. Do not delay obedience.\n- Tip 2 — Baptismal Anniversary: If you have been baptized, celebrate your \"spiritual birthday\" each year by sharing your testimony with someone.\n- Tip 3 — Sponsor a Candidate: If you are a mature believer, volunteer to disciple and prepare someone who is preparing for water baptism.\n\nReflection Questions (for group discussion):\n\n1. Why is water baptism considered an act of obedience rather than a means of salvation?\n2. What does it mean to be \"baptized into Christ's death\"?\n3. How is water baptism different from Holy Communion in its purpose and symbolism?\n4. Why do you think Jesus Himself was baptized even though He had no sin?\n5. What should a believer's life look like after baptism in terms of \"walking in newness of life\"?\n6. How would you respond to someone who says, \"I was baptized as a baby, so I don't need to be baptized again\"?\n7. What spiritual benefits have you experienced or heard others experience after water baptism?\n8. How can a believer prepare their heart before being baptized?\n9. Why is public baptism more impactful than private dedication?\n10. If you have not yet been baptized, what is holding you back? If you have, how has it marked your spiritual journey?",
          "quiz": {
            "title": "Topic 10 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "Water baptism identifies us with Christ's death, burial, and:",
                "type": "mcq",
                "options": [
                  "ascension",
                  "temptation",
                  "transfiguration",
                  "birth in Bethlehem",
                  "resurrection"
                ],
                "correctAnswer": 4,
                "explanation": "Romans 6:3-4 teaches that baptism identifies believers with Christ's death, burial, and resurrection."
              },
              {
                "questionText": "Water baptism is:",
                "type": "mcq",
                "options": [
                  "optional for believers",
                  "only for infants",
                  "a command of Christ",
                  "a means of earning salvation",
                  "a monthly ritual"
                ],
                "correctAnswer": 2,
                "explanation": "Water baptism is a command of Christ (Matthew 28:19), not an optional practice."
              },
              {
                "questionText": "The biblical pattern in Acts 8:36-38 is to:",
                "type": "mcq",
                "options": [
                  "baptize first, then believe",
                  "believe first, then be baptized",
                  "baptize infants only",
                  "be confirmed first",
                  "fast before baptizing"
                ],
                "correctAnswer": 1,
                "explanation": "Acts 8:36-38 shows the pattern of believing first, and then being baptized."
              },
              {
                "questionText": "Romans 6:4 -- we should walk in:",
                "type": "mcq",
                "options": [
                  "oldness of life",
                  "fear and bondage",
                  "newness of life",
                  "tradition",
                  "the law"
                ],
                "correctAnswer": 2,
                "explanation": "Romans 6:4 calls the baptized believer to walk in newness of life."
              },
              {
                "questionText": "Water baptism is described as a spiritual:",
                "type": "mcq",
                "options": [
                  "graduation",
                  "coronation",
                  "marriage",
                  "circumcision",
                  "adoption ceremony"
                ],
                "correctAnswer": 3,
                "explanation": "Baptism is described as a spiritual circumcision that cuts away the old life (Colossians 2:11-12)."
              }
            ]
          }
        },
        {
          "title": "Topic 11: Baptism of the Holy Spirit",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thoughts:\n\nThe baptism of the Holy Spirit is not a denominational doctrine; it is a divine empowerment that transforms ordinary believers into extraordinary witnesses.\n\nCore Teaching:\n\nThe baptism of the Holy Spirit is a distinct experience from the new birth. While every believer receives the Holy Spirit at salvation (Romans 8:9), the baptism of the Holy Spirit is an enduement of power for service (Acts 1:8). Jesus promised this baptism to His disciples before His ascension, and it was fulfilled on the Day of Pentecost (Acts 2:1-4).\n\nThe initial physical evidence of the baptism of the Holy Spirit is speaking in other tongues as the Spirit gives utterance (Acts 2:4; 10:44-46; 19:6). Tongues is a supernatural language that edifies the believer (1 Corinthians 14:4), enables deep intercession (Romans 8:26-27), and is a sign of covenant relationship with God.\n\nThe baptism of the Holy Spirit is available to every believer who asks (Luke 11:13). It is not earned by merit but received by faith. For the young believer, it is the ignition of spiritual fire. For the mature believer, it is the refueling station for sustained ministry. For the elder, it is the ongoing source of spiritual vitality and prophetic insight.\n\nThe Holy Spirit also distributes gifts for the edification of the church (1 Corinthians 12:4-11), produces fruit in the believer's character (Galatians 5:22-23), and leads us into all truth (John 16:13).\n\nKey Scriptures:\n\nActs 1:8 (KJV): \"But ye shall receive power, after that the Holy Ghost is come upon you: and ye shall be witnesses unto me both in Jerusalem, and in all Judaea, and in Samaria, and unto the uttermost part of the earth.\"\n\nActs 1:8 (AMP): \"But you will receive power and ability when the Holy Spirit comes upon you, and you will be My witnesses both in Jerusalem and in all Judea, and Samaria, and even to the ends of the earth.\"\n\nActs 2:4 (KJV): \"And they were all filled with the Holy Ghost, and began to speak with other tongues, as the Spirit gave them utterance.\"\n\nActs 2:4 (AMP): \"And they were all filled with the Holy Spirit and began to speak in other tongues, as the Spirit was giving them the ability to speak out.\"\n\nLuke 11:13 (KJV): \"If ye then, being evil, know how to give good gifts unto your children: how much more shall your heavenly Father give the Holy Spirit to them that ask him?\"\n\nLuke 11:13 (AMP): \"If you then, being evil, know how to give good gifts to your children, how much more will your heavenly Father give the Holy Spirit to those who ask Him?\"\n\nPut It Into Practice\n\nWorkgroup Task: Pentecost Revisited: In groups, study Acts 2:1-4, Acts 8:14-17, Acts 10:44-46, and Acts 19:1-6. Create a comparison chart showing: Who received the baptism, What happened, and What was the result. Present your findings and discuss how these patterns apply today.\n\nLife Application Tips:\n\n- Tip 1 — Ask and Receive: Set aside time this week to pray specifically for the baptism of the Holy Spirit. Ask your Pastor or a Spirit-filled believer to lay hands on you.\n- Tip 2 — Pray in Tongues Daily: If you have received the baptism, commit to praying in tongues for at least 10 minutes every day for the next 30 days.\n- Tip 3 — Teach Others: If you are Spirit-filled, identify one believer who has not yet received the baptism and walk with them toward this experience.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between being \"born of the Spirit\" and being \"baptized in the Spirit\"?\n2. Why did Jesus command His disciples to wait in Jerusalem until they received the Holy Spirit?\n3. What is the significance of speaking in tongues as the initial evidence of the baptism?\n4. How does the baptism of the Holy Spirit empower a believer for witness and ministry?\n5. What are the nine gifts of the Spirit, and which ones have you seen in operation?\n6. How can a believer who desires the baptism of the Holy Spirit position themselves to receive it?\n7. What role does the Holy Spirit play in guiding a believer's daily decisions?\n8. How does the fruit of the Spirit differ from the gifts of the Spirit?\n9. Why do some churches reject the baptism of the Holy Spirit with tongues, and how should we respond?\n10. If you have received the baptism, how has it changed your prayer life and ministry? If you have not, what steps will you take to receive it?",
          "quiz": {
            "title": "Topic 11 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "Acts 1:8 -- you shall receive ______ after the Holy Ghost comes upon you:",
                "type": "mcq",
                "options": [
                  "wealth",
                  "power",
                  "fame",
                  "knowledge of languages",
                  "perfect health"
                ],
                "correctAnswer": 1,
                "explanation": "Acts 1:8 promises power when the Holy Spirit comes upon a believer."
              },
              {
                "questionText": "The initial physical evidence is:",
                "type": "mcq",
                "options": [
                  "dancing",
                  "speaking in other tongues as the Spirit gives utterance",
                  "holy laughter",
                  "weeping",
                  "falling asleep"
                ],
                "correctAnswer": 1,
                "explanation": "Speaking in other tongues as the Spirit gives utterance is the initial physical evidence (Acts 2:4)."
              },
              {
                "questionText": "The promise was fulfilled on:",
                "type": "mcq",
                "options": [
                  "the Passover",
                  "the Sabbath",
                  "the Day of Pentecost",
                  "the Feast of Tabernacles",
                  "Easter Sunday"
                ],
                "correctAnswer": 2,
                "explanation": "The promise of the Spirit's baptism was fulfilled on the Day of Pentecost (Acts 2:1-4)."
              },
              {
                "questionText": "Luke 11:13 -- the Father gives the Holy Spirit to them that:",
                "type": "mcq",
                "options": [
                  "pay their tithes",
                  "fast for forty days",
                  "are ordained",
                  "ask Him",
                  "live perfect lives"
                ],
                "correctAnswer": 3,
                "explanation": "Luke 11:13 says the Father gives the Holy Spirit to those who ask Him."
              },
              {
                "questionText": "1 Corinthians 14:4 -- speaking in tongues edifies:",
                "type": "mcq",
                "options": [
                  "the congregation",
                  "the pastor",
                  "the believer who speaks",
                  "the visitors",
                  "the angels only"
                ],
                "correctAnswer": 2,
                "explanation": "1 Corinthians 14:4 says one who speaks in tongues edifies himself."
              }
            ]
          }
        },
        {
          "title": "Topic 12: Tithes & Offering",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thoughts:\n\nLearners will understand tithing as an act of covenant obedience, offering as worship, and both as keys to financial breakthrough and kingdom financing.\n\nKey Scriptures (KJV & AMP):\n\n\"Bring ye all the tithes into the storehouse, that there may be meat in mine house, and prove me now herewith, saith the Lord of hosts, if I will not open you the windows of heaven, and pour you out a blessing, that there shall not be room enough to receive it.\" — Malachi 3:10 (KJV)\n\n\"Bring all the tithes into the storehouse, that there may be food in My house, and prove Me now by it, says the Lord of hosts, if I will not open the windows of heaven for you and pour you out a blessing, that there shall not be room enough to receive it.\" — Malachi 3:10 (AMP)\n\n\"But this I say, He which soweth sparingly shall reap also sparingly; and he which soweth bountifully shall reap also bountifully. Every man according as he purposeth in his heart, so let him give; not grudgingly, or of necessity: for God loveth a cheerful giver.\" — 2 Corinthians 9:6-7 (KJV)\n\n\"But I say this: He who sows sparingly will also reap sparingly, and he who sows bountifully will also reap bountifully. Let each one give as he has made up his own mind, not reluctantly or under compulsion, for God loves a cheerful giver.\" — 2 Corinthians 9:6-7 (AMP)\n\nCore Teachings:\n\nThe tithe (10%) belongs to God (Lev. 27:30; Mal. 3:8-10). It is not a gift but a return of what is already His. Tithing:\n\n- Honors God as Lord of our finances (Prov. 3:9-10)\n\n- Opens the windows of heaven (Mal. 3:10)\n\n- Rebukes the devourer (Mal. 3:11)\n\n- Provokes supernatural multiplication (Luke 6:38)\n\nOfferings are given above the tithe as worship, thanksgiving, and seed-sowing (2 Cor. 9:6-7). Teach that giving must be cheerful, purposeful, and proportionate.\n\nGenerational Bridge: Older members often have long histories of faithful tithing, with testimonies of provision. Younger members may struggle with student debt, startup salaries, and materialism. Teach that tithing is not about amount but about lordship.\n\nPut It Into Practice\n\nWorkgroup Task: \"Kingdom Budget.\" Groups create a sample monthly budget for a young professional, a family, and a retiree, showing how tithe, offering, savings, and expenses can coexist. Discuss practical strategies for faithful giving in each life stage.\n\nLife Application Tips:\n\n- No one should manipulate or shame you. We need the blessing, not the burden.\n- Share testimonies (your own or others') of God's faithfulness in finances.\n- Talk about payment options such as envelopes, online giving instructions, or mobile payment options or others you feel comfortable with.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between a tithe and an offering?\n2. Why does Malachi 3:8 call withholding the tithe \"robbing God\"?\n3. How does tithing demonstrate that Jesus is Lord of every area of life, including money?\n4. What does it mean that God invites us to \"prove Him\" in Malachi 3:10?\n5. How does the principle of sowing and reaping apply to finances?\n6. Can a person tithe their time or talents instead of money? Why or why not?\n7. What are the promised blessings for faithful tithing according to Malachi 3:10-11?\n8. How does cheerful giving differ from giving under compulsion?\n9. What should a believer do if they are in debt—stop tithing until they are free, or continue?\n10. How can a new believer begin the discipline of tithing when their income is irregular?",
          "quiz": {
            "title": "Topic 12 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "The tithe is what percentage of income?",
                "type": "mcq",
                "options": [
                  "5%",
                  "10%",
                  "20%",
                  "25%",
                  "Any amount"
                ],
                "correctAnswer": 1,
                "explanation": "The tithe is ten percent of income, belonging to God (Leviticus 27:30)."
              },
              {
                "questionText": "Malachi 3:10 -- bring all the tithes into the:",
                "type": "mcq",
                "options": [
                  "bank",
                  "marketplace",
                  "home",
                  "storehouse",
                  "government treasury"
                ],
                "correctAnswer": 3,
                "explanation": "Malachi 3:10 instructs believers to bring the whole tithe into the storehouse."
              },
              {
                "questionText": "God promises to open:",
                "type": "mcq",
                "options": [
                  "the doors of the church",
                  "the windows of heaven",
                  "the gates of the city",
                  "the banks",
                  "the wells of water"
                ],
                "correctAnswer": 1,
                "explanation": "Malachi 3:10 promises that God will open the windows of heaven over faithful tithers."
              },
              {
                "questionText": "2 Corinthians 9:7 -- God loves a:",
                "type": "mcq",
                "options": [
                  "rich giver",
                  "cheerful giver",
                  "quiet giver",
                  "weekly giver",
                  "large giver"
                ],
                "correctAnswer": 1,
                "explanation": "2 Corinthians 9:7 says God loves a cheerful giver."
              },
              {
                "questionText": "Malachi 3:8 describes withholding the tithe as:",
                "type": "mcq",
                "options": [
                  "saving wisely",
                  "investing",
                  "robbing God",
                  "storing up",
                  "sowing sparingly"
                ],
                "correctAnswer": 2,
                "explanation": "Malachi 3:8 calls withholding the tithe \"robbing God.\""
              }
            ]
          }
        }
      ]
    },
    {
      "title": "The Dominion Bible Faith International Ministry",
      "lectures": [
        {
          "title": "Topic 13: The Secret of Dominion",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thoughts:\n\nDominion is not mystical but practical—rooted in faith, the Word, prayer, and identity.\n\nKey Scriptures (KJV & AMP):\n\n\"For whatsoever is born of God overcometh the world: and this is the victory that overcometh the world, even our faith.\" — 1 John 5:4 (KJV)\n\n\"For whatever is born of God is victorious over the world; and this is the victory that conquers the world, even our faith.\" — 1 John 5:4 (AMP)\n\n\"So then faith cometh by hearing, and hearing by the word of God.\"\n\n— Romans 10:17 (KJV)\n\n\"So faith comes by hearing, and hearing by the word of Christ.\"\n\n— Romans 10:17 (AMP)\n\nCore Teaching:\n\nThe secret of dominion is no secret to those who know God's Word. It operates through:\n\ni.  Faith — believing God's Word above circumstances (Heb. 11:1, 3; Rom. 10:17)\n\nii. The Spoken Word — confessing faith-filled words (Mark 11:23; 2 Cor. 4:13)\n\niii. Knowledge of Identity — knowing who you are in Christ (Eph. 1:17-23; Col. 1:27)\n\niv. Prayer — authoritative prayer, not begging (Eph. 6:18; James 5:16)\n\nv.  The Name of Jesus — operating in delegated authority (Phil. 2:9-10; John 14:13-14)\n\nGenerational Bridge: Older members may emphasize \"speaking the Word\" and prayer. Younger members may emphasize knowledge, research, and strategic thinking. Show that dominion integrates spiritual authority with intellectual excellence.\n\nPut It Into Practice\n\nWorkgroup Task: \"Dominion Case Studies.\" Groups receive three real-life scenarios: (1) a medical diagnosis, (2) a financial crisis, (3) a spiritual attack. Using the five secrets of dominion, they draft a practical response plan with Scriptures and confessions for each.\n\nLife Application Tips:\n\n- Dominion should never sound like positive thinking or a New Age manifestation.\n- Anchor every principle in Scripture on the current subject and share.\n- Build a \"dominion library\" of faith-building books and sermons.\"\n\nReflection Questions (for group discussion):\n\n1. What does 1 John 5:4 mean when it says our faith is \"the victory\"?\n2. How does Romans 10:17 connect dominion to hearing the Word of God?\n3. Why is confession (speaking) essential to exercising faith?\n4. What is the difference between praying \"from victory\" and praying \"for victory\"?\n5. How does knowing your identity in Christ change the way you face challenges?\n6. What role does the Name of Jesus play in the believer's authority?\n7. How can a believer maintain faith when circumstances contradict the Word?\n8. What are the dangers of trying to exercise dominion without the Word or the Spirit?\n9. How does the \"secret of dominion\" apply to family life, business, and health?\n10. Write three faith confessions based on Scripture that you will speak daily this week.",
          "quiz": {
            "title": "Topic 13 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "1 John 5:4 -- the victory that overcomes the world is:",
                "type": "mcq",
                "options": [
                  "our works",
                  "our faith",
                  "our strength",
                  "our money",
                  "our wisdom"
                ],
                "correctAnswer": 1,
                "explanation": "1 John 5:4 says the victory that overcomes the world is our faith."
              },
              {
                "questionText": "Romans 10:17 -- faith comes by hearing, and hearing by:",
                "type": "mcq",
                "options": [
                  "dreams",
                  "fasting",
                  "working hard",
                  "the word of God",
                  "traveling"
                ],
                "correctAnswer": 3,
                "explanation": "Romans 10:17 says faith comes by hearing the word of God."
              },
              {
                "questionText": "Which is NOT among the five secrets of dominion?",
                "type": "mcq",
                "options": [
                  "Faith",
                  "The spoken Word",
                  "Knowledge of identity",
                  "Prayer",
                  "Fasting"
                ],
                "correctAnswer": 4,
                "explanation": "The five secrets taught are faith, the spoken Word, knowledge of identity, prayer, and the Name of Jesus -- fasting is not among them."
              },
              {
                "questionText": "Mark 11:23 connects faith with:",
                "type": "mcq",
                "options": [
                  "keeping silent",
                  "confession (speaking faith-filled words)",
                  "hiding the vision",
                  "waiting patiently",
                  "paying vows"
                ],
                "correctAnswer": 1,
                "explanation": "Mark 11:23 connects exercising faith with speaking faith-filled words."
              },
              {
                "questionText": "Believers exercise delegated authority through:",
                "type": "mcq",
                "options": [
                  "their own names",
                  "the names of angels",
                  "their church titles",
                  "their ancestry",
                  "the Name of Jesus"
                ],
                "correctAnswer": 4,
                "explanation": "Delegated authority is exercised through the Name of Jesus (Philippians 2:9-10)."
              }
            ]
          }
        },
        {
          "title": "Topic 14: The Journey (Preview of the Ministry)",
          "type": "text",
          "duration": 600,
          "textContent": "Opening thoughts:\n\nLearners will appreciate the breadth of DCIC's ministry and find their place within the vision.\n\nKey Scriptures (KJV & AMP):\n\n\"And he gave some, apostles; and some, prophets; and some, evangelists; and some, pastors and teachers; For the perfecting of the saints, for the work of the ministry, for the edifying of the body of Christ.\" — Ephesians 4:11-12 (KJV)\n\n\"And His gifts were that some should be apostles, prophets, evangelists, pastors, and teachers, for the full equipment and perfecting of the saints, for the work of ministering, for building up the body of Christ.\"\n\n— Ephesians 4:11-12 (AMP)\n\nCore Teaching:\n\nDCIC is not just a Sunday church; it is a ministry ecosystem:\n\n- Dominion Bible Faith Institute (DOBFI) — Bible school for doctrinal depth\n\n- John Praise World Outreach (JOPWO) — apostolic outreach and media\n\n- International Leadership Conferences of Ministries (ILCOM) — monthly ministerial fellowship and impartation\n\n- Weapons of Influence School of Deliverance — Thursday morning deliverance school\n\n- Festival of Music International (FOMI) — all-night worship every September, ushering Nigeria into independence day with praise\n\n- Television and Cable Networks — Global Gospel Transmission\n\n- Generational Bridge: Older members may connect with ILCOM and Bible school. Younger members may connect with media, music, and technology. Both are needed for the \"total man\" vision.\n\nPut It Into Practice\n\nWorkgroup Task: \"Ministry Mapping.\" Groups create a visual map of DCIC's ministry arms, connecting each arm to a specific need in society (e.g., DOBFI → ignorance; FOMI → national transformation; JOPWO → global evangelism). Present with passion.\n\nLife Application Tips:\n\n- Invite a representative from each ministry arm to give a 2-minute pitch, if possible.\n- Have sign-up sheets or QR codes ready for immediate engagement.\n- Remind learners that every ministry arm needs both participants and volunteers.\n\nReflection Questions (for group discussion):\n\n1. Why does DCIC operate multiple ministry arms instead of just Sunday services?\n2. How does DOBFI help believers grow from converts to disciples?\n3. What is the significance of FOMI being held on the eve of Nigeria's independence?\n4. How does ILCOM fulfill the unity of the Body of Christ across denominations?\n5. What role does media and technology play in fulfilling the Great Commission today?\n6. How can a new member discover which ministry arm fits their calling?\n7. Why is deliverance ministry still relevant in the 21st century?\n8. What does Ephesians 4:12 mean by \"perfecting of the saints\"?\n9. How does the \"total man\" vision require more than one type of ministry expression?\n10. Which ministry arm excites you most, and what is your first step to get involved?",
          "quiz": {
            "title": "Topic 14 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "DOBFI is the official ______ of the church:",
                "type": "mcq",
                "options": [
                  "choir arm",
                  "Bible school",
                  "welfare unit",
                  "media house",
                  "drama group"
                ],
                "correctAnswer": 1,
                "explanation": "DOBFI (Dominion Bible Faith Institute) is the church's official theological and leadership training school."
              },
              {
                "questionText": "FOMI is held every:",
                "type": "mcq",
                "options": [
                  "December",
                  "January",
                  "September",
                  "April",
                  "June"
                ],
                "correctAnswer": 2,
                "explanation": "The Festival of Music International (FOMI) is held every September."
              },
              {
                "questionText": "FOMI leads the nation into:",
                "type": "mcq",
                "options": [
                  "Christmas Day",
                  "October 1, Nigeria's Independence Day",
                  "Easter Sunday",
                  "New Year's Day",
                  "Democracy Day"
                ],
                "correctAnswer": 1,
                "explanation": "FOMI is an all-night worship event designed to lead the nation into October 1st, Nigeria's Independence Day."
              },
              {
                "questionText": "JOPWO stands for:",
                "type": "mcq",
                "options": [
                  "Joint Praise Worship Outreach",
                  "John Praise World Outreach",
                  "Jubilee Prayer Watch Organization",
                  "Junior Pastors' World Order",
                  "Jesus Power Worldwide"
                ],
                "correctAnswer": 1,
                "explanation": "JOPWO stands for John Praise World Outreach, an apostolic television and media ministry."
              },
              {
                "questionText": "ILCOM is described as a gathering:",
                "type": "mcq",
                "options": [
                  "weekly choir",
                  "yearly building",
                  "monthly interdenominational",
                  "daily market",
                  "quarterly finance"
                ],
                "correctAnswer": 2,
                "explanation": "ILCOM (International Leadership Conferences of Ministries) is a monthly interdenominational leadership gathering."
              }
            ]
          }
        },
        {
          "title": "Topic 15: The Place of Service",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thoughts:\n\nEvery believer is a minister; identify their spiritual gifts and commit to serving in a department.\n\nKey Scriptures (KJV & AMP):\n\n\"As every man hath received the gift, even so minister the same one to another, as good stewards of the manifold grace of God.\"\n\n— 1 Peter 4:10 (KJV)\n\n\"As each of you has received a gift—a particular spiritual ability—a grace-given spiritual endowment, employ it for one another as good stewards of God's many-sided grace.\" — 1 Peter 4:10 (AMP)\n\n\"For we are his workmanship, created in Christ Jesus unto good works, which God hath before ordained that we should walk in them.\"\n\n— Ephesians 2:10 (KJV)\n\n\"For we are God's own handiwork, His workmanship, recreated in Christ Jesus, born anew that we may do those good works which God predestined for us.\" — Ephesians 2:10 (AMP)\n\nCore Teaching:\n\nThere are no laypeople in the Kingdom—only ministers with different assignments. Service is not a punishment but a privilege. Every believer has received at least one spiritual gift (1 Cor. 12:7; Rom. 12:6-8; Eph. 4:11). The place of service is where:\n\n- Your gift meets the church's need\n\n- Your passion meets God's purpose\n\n- Your growth is accelerated.\n\n- Your dominion is demonstrated.\n\nGenerational Bridge:\n\nOlder members may serve from duty and loyalty. Younger members may serve from passion and impact. Both are valid when submitted to leadership and aligned with gifting.\n\nPut It Into Practice\n\nWorkgroup Task: \"Gift Discovery Workshop.\" Groups read Romans 12:6-8, 1 Corinthians 12:4-11, and Ephesians 4:11. Each person identifies two gifts they believe they have and one they would like to develop. The group confirms or adds insight.\n\nLife Application Tips:\n\n- Have a current list of open volunteer positions.\n- Emphasize that character precedes gifting.\n- Match people to roles, but also challenge them to grow into new areas.\n\nReflection Questions (for group discussion):\n\n1. What is the difference between a \"layperson\" and a \"minister\" in the New Testament?\n2. How does 1 Corinthians 12:7 say spiritual gifts are distributed?\n3. Why is serving others described as being a \"steward of God's grace\"?\n4. What happens to a believer who refuses to serve in the local church?\n5. How can you discover your spiritual gifts if you are unsure what they are?\n6. What is the difference between serving out of obligation and serving out of calling?\n7. How does Ephesians 2:10 show that good works were planned by God before you were born?\n8. Why is it important to serve under authority rather than independently?\n9. How does serving in church prepare you for leadership in the marketplace?\n10. What is one step you will take this month to begin serving in DCIC?",
          "quiz": {
            "title": "Topic 15 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "1 Peter 4:10 -- as every man has received the gift, he should:",
                "type": "mcq",
                "options": [
                  "hide it",
                  "sell it",
                  "display it",
                  "bury it",
                  "minister the same one to another"
                ],
                "correctAnswer": 4,
                "explanation": "1 Peter 4:10 calls every believer to minister their gift to one another as good stewards of grace."
              },
              {
                "questionText": "Ephesians 2:10 -- created in Christ Jesus unto:",
                "type": "mcq",
                "options": [
                  "fame",
                  "good works",
                  "riches",
                  "rest",
                  "ruling nations"
                ],
                "correctAnswer": 1,
                "explanation": "Ephesians 2:10 says believers are created in Christ Jesus unto good works."
              },
              {
                "questionText": "There are no laypeople in the Kingdom -- only:",
                "type": "mcq",
                "options": [
                  "volunteers",
                  "employees",
                  "guests",
                  "spectators",
                  "ministers with different assignments"
                ],
                "correctAnswer": 4,
                "explanation": "The lesson teaches that every believer is a minister with a different assignment, not a mere layperson."
              },
              {
                "questionText": "The place of service is where your gift meets:",
                "type": "mcq",
                "options": [
                  "your ambition",
                  "your comfort zone",
                  "your schedule",
                  "the church's need",
                  "your friends"
                ],
                "correctAnswer": 3,
                "explanation": "The place of service is described as where your gift meets the church's need."
              },
              {
                "questionText": "Your growth is accelerated in:",
                "type": "mcq",
                "options": [
                  "the place of service",
                  "your sleep",
                  "your travels",
                  "the marketplace",
                  "your hobbies"
                ],
                "correctAnswer": 0,
                "explanation": "The lesson teaches that spiritual growth is accelerated in the place of service."
              }
            ]
          }
        },
        {
          "title": "Topic 16: The Departments",
          "type": "text",
          "duration": 600,
          "textContent": "Opening Thoughts:\n\nUnderstand the organizational structure of DCIC, respect the chain of authority, and integrate into the body through a department.\n\nKey Scriptures (KJV & AMP):\n\n\"Let all things be done decently and in order.\" — 1 Corinthians 14:40 (KJV)\n\n\"But all things should be done with regard to decency and propriety and in an orderly fashion.\" — 1 Corinthians 14:40 (AMP)\n\n\"Obey them that have the rule over you, and submit yourselves: for they watch for your souls, as they that must give account, that they may do it with joy, and not with grief: for that is unprofitable for you.\" — Hebrews 13:17 (KJV)\n\n\"Obey your spiritual leaders and submit to them, for they are constantly keeping watch over your souls and guarding them, as those who will have to render an account. Do this so that they may have joy in doing it, not sighing and groaning, for that would not be profitable to you.\"\n\n— Hebrews 13:17 (AMP)\n\nCore Teaching:\n\nDepartments exist to ensure decency and order (1 Cor. 14:40). Typical DCIC departments may include:\n\n- Pastoral / Ministerial — spiritual oversight\n\n- Administration — Order and Logistics\n\n- Finance / Treasury — stewardship of resources\n\n- Ushering /Protocol — welcoming and attending to guests\n\n- Media / Technical — sound, video, social media\n\n- Choir / Music / Worship — praise and worship\n\n- Evangelism / Follow-up — soul-winning and assimilation\n\n- Children / Teen / Youth — age-appropriate discipleship\n\n- Women (Women of Influence)/Men (Wisemen) - Gender-appropriate discipleship\n\n- Trybes - Evangelism and Follow-up Platforms\n\n- Security - Oversee the security of lives and properties within the church premises\n\n- Technical - Management of Electronics and Electrical Devices\n\n- Temple Keepers - Arrangement and Sanitary Conditions of the Church Premises\n\n- GloryWood - Glory House Drama Group\n\n- Prayer / Intercession — Spiritual Covering\n\n- Marriage Committee - Marriage Counseling\n\n- Welfare (Dorcas Committee) — Care and Support\n\n- Project Committee - Coordinate Church Projects\n\nGenerational Bridge: Older members may gravitate toward pastoral care, welfare, and administration. Younger members may gravitate toward media, youth ministry, and the creative arts. Both are vital.\n\nPut It Into Practice\n\nWorkgroup Task: \"Department Pitch.\" Each group is assigned a department (real or hypothetical). They create a 3-minute recruitment pitch explaining: (1) the department's purpose, (2) the type of person who thrives there, (3) the impact on the church and community, and (4) how to join.\n\nLife Application Tips:\n\n- Department heads or representatives to present briefly.\n- Provide a clear pathway for joining: \"Talk to Pastor X, fill out Form Y, attend Training Z.\"\n- Celebrate those who have served faithfully for years.\n\nReflection Questions (for group discussion):\n\n1. Why does God care about \"order\" in the church?\n2. How does Hebrews 13:17 describe the responsibility of leaders toward members?\n3. What is the member's responsibility toward leaders according to Hebrews 13:17?\n4. How do departments prevent chaos and promote excellence in church operations?\n5. What happens when a church grows but does not organize into departments?\n6. How can a member serve in a department while still respecting the overall vision?\n7. What qualities should a department head possess?\n8. Why is it important to serve in one department faithfully rather than floating between many?\n9. How does excellence in church departments reflect on the God we serve?\n10. Which department aligns with your gifts, and who will you contact this week to join?",
          "quiz": {
            "title": "Topic 16 Quiz",
            "passingScore": 70,
            "questions": [
              {
                "questionText": "1 Corinthians 14:40 -- all things should be done:",
                "type": "mcq",
                "options": [
                  "loudly",
                  "quickly",
                  "decently and in order",
                  "secretly",
                  "freely"
                ],
                "correctAnswer": 2,
                "explanation": "1 Corinthians 14:40 instructs that all things be done decently and in order."
              },
              {
                "questionText": "Hebrews 13:17 -- obey those who have the rule over you and:",
                "type": "mcq",
                "options": [
                  "pay them salaries",
                  "test them constantly",
                  "submit yourselves",
                  "avoid them",
                  "replace them"
                ],
                "correctAnswer": 2,
                "explanation": "Hebrews 13:17 calls believers to obey and submit to those who watch over their souls."
              },
              {
                "questionText": "According to Hebrews 13:17, leaders watch over your:",
                "type": "mcq",
                "options": [
                  "money",
                  "clothes",
                  "attendance",
                  "behavior",
                  "souls"
                ],
                "correctAnswer": 4,
                "explanation": "Hebrews 13:17 says leaders watch over your souls, as those who must give account."
              },
              {
                "questionText": "Which is NOT listed as a DCIC department?",
                "type": "mcq",
                "options": [
                  "Ushering/Protocol",
                  "Welfare (Dorcas Committee)",
                  "Political Campaign Unit",
                  "Children's Ministry",
                  "GloryWood"
                ],
                "correctAnswer": 2,
                "explanation": "The manual lists departments such as ushering, welfare, children's ministry, and GloryWood -- a political campaign unit is not among them."
              },
              {
                "questionText": "GloryWood is the:",
                "type": "mcq",
                "options": [
                  "security unit",
                  "finance team",
                  "cleaning crew",
                  "choir",
                  "drama group of Glory House"
                ],
                "correctAnswer": 4,
                "explanation": "GloryWood is described as the drama group of Glory House."
              }
            ]
          }
        }
      ]
    }
  ],
  "finalQuiz": {
    "title": "Foundation & Membership / Discipleship Class -- Final Assessment",
    "passingScore": 70,
    "questions": [
      {
        "questionText": "In what year did Brother John Praise receive the vision that birthed DCIC?",
        "type": "mcq",
        "options": [
          "1985",
          "1986",
          "1987",
          "1988",
          "1989"
        ],
        "correctAnswer": 1,
        "explanation": "The founding vision was received in 1986, a year before the Bwari Bible study began in 1987."
      },
      {
        "questionText": "The 1986 divine call was to raise a people of power who would walk as:",
        "type": "mcq",
        "options": [
          "kings and priests",
          "servants only",
          "prophets only",
          "teachers only",
          "strangers"
        ],
        "correctAnswer": 0,
        "explanation": "God called for a people of power who would walk as kings and priests on the earth."
      },
      {
        "questionText": "The DCIC vision is about reaching the ______ with the total gospel.",
        "type": "mcq",
        "options": [
          "rich",
          "total man",
          "young",
          "clergy",
          "aged"
        ],
        "correctAnswer": 1,
        "explanation": "The vision is to reach \"the total man\" -- spirit, soul, and body -- with the total gospel."
      },
      {
        "questionText": "Acts 2:42 -- the early church continued steadfastly in the apostles' doctrine and:",
        "type": "mcq",
        "options": [
          "buying and selling",
          "fasting only",
          "temple rituals",
          "circumcision",
          "fellowship, breaking of bread, and prayers"
        ],
        "correctAnswer": 4,
        "explanation": "Acts 2:42 lists doctrine, fellowship, the breaking of bread, and prayers as marks of the early church."
      },
      {
        "questionText": "Genesis 1:26 -- man was given dominion over:",
        "type": "mcq",
        "options": [
          "other men only",
          "angels",
          "nothing at all",
          "the seas only",
          "the fish, fowl, cattle, and all the earth"
        ],
        "correctAnswer": 4,
        "explanation": "Genesis 1:26 gives mankind dominion over the fish, the fowl, the cattle, and all the earth."
      },
      {
        "questionText": "To be justified means to be:",
        "type": "mcq",
        "options": [
          "made religious",
          "declared righteous and free from guilt",
          "baptized in water",
          "registered in church",
          "ordained as a pastor"
        ],
        "correctAnswer": 1,
        "explanation": "Justification means being declared righteous and free from guilt before God."
      },
      {
        "questionText": "2 Corinthians 5:17 -- anyone in Christ is a:",
        "type": "mcq",
        "options": [
          "better sinner",
          "visitor",
          "servant",
          "new creature",
          "upgraded man"
        ],
        "correctAnswer": 3,
        "explanation": "2 Corinthians 5:17 declares that anyone in Christ is a new creature; old things have passed away."
      },
      {
        "questionText": "1 Peter 2:2 -- newborn babes should desire the sincere milk of:",
        "type": "mcq",
        "options": [
          "the word",
          "water",
          "honey",
          "solid meat",
          "wine"
        ],
        "correctAnswer": 0,
        "explanation": "1 Peter 2:2 calls believers to desire the sincere milk of the word in order to grow."
      },
      {
        "questionText": "Communion was instituted by Jesus on:",
        "type": "mcq",
        "options": [
          "Easter morning",
          "the Sabbath day",
          "the night in which He was betrayed",
          "the Day of Pentecost",
          "the day of Ascension"
        ],
        "correctAnswer": 2,
        "explanation": "1 Corinthians 11:23 records that communion was instituted on the night Jesus was betrayed."
      },
      {
        "questionText": "Water baptism identifies us with Christ's death, burial, and:",
        "type": "mcq",
        "options": [
          "ascension",
          "temptation",
          "transfiguration",
          "birth in Bethlehem",
          "resurrection"
        ],
        "correctAnswer": 4,
        "explanation": "Romans 6:3-4 teaches that baptism identifies believers with Christ's death, burial, and resurrection."
      },
      {
        "questionText": "Acts 1:8 -- you shall receive ______ after the Holy Ghost comes upon you:",
        "type": "mcq",
        "options": [
          "wealth",
          "power",
          "fame",
          "knowledge of languages",
          "perfect health"
        ],
        "correctAnswer": 1,
        "explanation": "Acts 1:8 promises power when the Holy Spirit comes upon a believer."
      },
      {
        "questionText": "The tithe is what percentage of income?",
        "type": "mcq",
        "options": [
          "5%",
          "10%",
          "20%",
          "25%",
          "Any amount"
        ],
        "correctAnswer": 1,
        "explanation": "The tithe is ten percent of income, belonging to God (Leviticus 27:30)."
      },
      {
        "questionText": "1 John 5:4 -- the victory that overcomes the world is:",
        "type": "mcq",
        "options": [
          "our works",
          "our faith",
          "our strength",
          "our money",
          "our wisdom"
        ],
        "correctAnswer": 1,
        "explanation": "1 John 5:4 says the victory that overcomes the world is our faith."
      },
      {
        "questionText": "DOBFI is the official ______ of the church:",
        "type": "mcq",
        "options": [
          "choir arm",
          "Bible school",
          "welfare unit",
          "media house",
          "drama group"
        ],
        "correctAnswer": 1,
        "explanation": "DOBFI (Dominion Bible Faith Institute) is the church's official theological and leadership training school."
      },
      {
        "questionText": "1 Peter 4:10 -- as every man has received the gift, he should:",
        "type": "mcq",
        "options": [
          "hide it",
          "sell it",
          "display it",
          "bury it",
          "minister the same one to another"
        ],
        "correctAnswer": 4,
        "explanation": "1 Peter 4:10 calls every believer to minister their gift to one another as good stewards of grace."
      },
      {
        "questionText": "1 Corinthians 14:40 -- all things should be done:",
        "type": "mcq",
        "options": [
          "loudly",
          "quickly",
          "decently and in order",
          "secretly",
          "freely"
        ],
        "correctAnswer": 2,
        "explanation": "1 Corinthians 14:40 instructs that all things be done decently and in order."
      }
    ]
  }
};
